﻿import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Send, ShoppingCart, Settings } from "lucide-react";
import ReactJson from "@microlink/react-json-view";
import { toast } from "react-toastify";

import ListScaffold from "@/components/desktop/list/table/ListScaffold";
import EditableDateCell from "@/components/desktop/list/table/cells/DateCell/EditableDateCell";
import JsonCell from "@/components/desktop/list/table/cells/JsonCell/JsonCell";
import CustomModalForm from "@/components/desktop/form/modal/CustomModalForm/CustomModalForm";
import CustomBadge from "@/components/desktop/badge/CustomBadge";
import CustomDropdown from "@/components/desktop/list/dropdown/CustomDropdown";
import CustomButton from "@/components/desktop/button/CustomButton";

import { dateToLocale } from "@/services/Utils";
import { useIntegrationUsages, useIntegrationUsagesLookups, useSaveIntegrationParams, useResendIntegration } from "@/hooks/integrations/useIntegration";
import useListController from "@/hooks/list/useListController";

import { base_table_purchase } from "../integrations.constants";
import { base_filter } from "./integration-usage.constants";

const IntegrationUsage = () => {
	const navigate = useNavigate();
	const { option, element_id } = useParams();

	const isOpen = option === "aplicaciones";

	const { data: lookups, isLoading: isLoadingLookups } = useIntegrationUsagesLookups();

	const {
		isFilterOpen,
		filtersState,
		tableState,
		appliedFilterParams,
		openFilter,
		closeFilter,
		handleFilterChange,
		handleApplyFilters,
		handleClearAllFilters,
		handleClearOneFilter,
		reactTableSort,
		handlePageChange,
		handlePageSizeChange,
		handleSortByChange,
		setFilterOptions,
	} = useListController({
		baseFilter: base_filter,
		baseTable: base_table_purchase,
	});

	const { data, isLoading } = useIntegrationUsages(
		{
			...appliedFilterParams,
			...tableState,
		},
		isOpen && !isLoadingLookups,
	);

	const { mutate: saveParams, isLoading: isLoadingSave } = useSaveIntegrationParams();
	const { mutate: resendIntegration, isLoading: isLoadingResend } = useResendIntegration();

	useEffect(() => {
		if (lookups?.balances && Array.isArray(lookups.balances)) {
			setFilterOptions("balance_id", lookups.balances);
		}
		if (lookups?.integrations && Array.isArray(lookups.integrations)) {
			setFilterOptions("integration_id", lookups.integrations);
		}
	}, [lookups, setFilterOptions]);

	const onClose = () => {
		navigate("/configuracion/integraciones");
	};

	const saveParam = (props) => {
		const payload = {
			filters: { ...appliedFilterParams, ...tableState },
			id: props.recordId,
			field: props.fieldName,
			value: props.newValue,
		};

		saveParams(payload, {
			onSuccess: () => {
				toast.success("Se ha guardado el parámetro correctamente");
			},
			onError: (error) => {
				const errorMsg = error?.response?.data?.message || error?.message || "Error desconocido, contacte al administrador";
				toast.error(errorMsg);
			},
		});
	};

	const handleResend = (integration_queue_id) => {
		const payload = {
			filters: { ...appliedFilterParams, ...tableState },
			id: integration_queue_id,
		};

		resendIntegration(payload, {
			onSuccess: () => {
				toast.success("Se ha reenviado la aplicación correctamente");
			},
			onError: (error) => {
				const errorMsg = error?.response?.data?.message || error?.message || "Error desconocido, contacte al administrador";
				toast.error(errorMsg);
			},
		});
	};

	if (!isOpen) return null;

	const columns = [
		{
			Header: "Fecha",
			accessor: "created_at",
			width: 100,
			Cell: (cell) => {
				return (
					<EditableDateCell
						value={cell.value}
						row={cell.row}
						fieldName="created_at"
						onSave={saveParam}
						displayFormatter={dateToLocale}
						getRecordId={(original) => original.id}
					/>
				);
			},
		},
		{
			Header: "Balanza",
			accessor: "name",
			width: 100,
			Cell: ({ value }) => {
				return <span>{String(value || "")}</span>;
			},
		},
		{
			Header: "Enviado",
			accessor: "body_send",
			disableSortBy: true,
			width: 300,
			Cell: ({ row }) => {
				const value = row.original.body_send;
				const message = row.original.message;
				let parsed;
				try {
					parsed = typeof value === "string" ? JSON.parse(value) : value;
				} catch {
					parsed = null;
				}
				if (parsed && typeof parsed === "object") {
					return <ReactJson src={parsed} collapsed={true} />;
				}
				const messageStr = typeof message === "object" ? JSON.stringify(message, null, 2) : String(message || "");
				return <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0 }}>{messageStr}</pre>;
			},
		},
		{
			Header: "Respuesta",
			accessor: "message_response",
			disableSortBy: true,
			width: 300,
			Cell: ({ row }) => {
				return (
					<JsonCell
						message={row.original.message}
						raw={row.original.message_response}
						json={row.original.message_response_json}
						initialMode="raw"
						maxWidth={300}
					/>
				);
			},
		},
		{
			Header: "Cód.",
			accessor: "code",
			disableSortBy: true,
			width: 80,
			Cell: ({ value }) => {
				return <span>{String(value || "")}</span>;
			},
		},
		{
			Header: "Estado",
			accessor: "completed",
			disableSortBy: true,
			width: 100,
			Cell: ({ value }) => {
				const isCompleted = Boolean(value);
				return (
					<CustomBadge variant={isCompleted ? "success" : "danger"} size="md">
						{isCompleted ? "Completada" : "Pendiente"}
					</CustomBadge>
				);
			},
		},
		{
			Header: "",
			disableSortBy: true,
			accessor: "id",
			width: 50,
			Cell: ({ value }) => {
				return (
					<div style={{ textAlign: "center" }}>
						<CustomDropdown
							title="Acciones"
							items={[
								{
									label: "Forzar Reenvío",
									action: () => handleResend(value),
									icon: Send,
								},
							]}
							align="right"
						/>
					</div>
				);
			},
		},
	];

	const isLoadingFinal = isLoading || isLoadingLookups || isLoadingSave || isLoadingResend;

	return (
		<CustomModalForm
			open={isOpen}
			onClose={onClose}
			title={"Aplicaciones Enviadas"}
			size="xxl"
			onSubmit={null}
			isLoading={isLoadingFinal}
			gridTable={false}
			showSave={false}
			footerLeft={
				<>
					<CustomButton
						variant="secondary"
						disabled={isLoading}
						startIcon={<Settings size={16} />}
						onClick={() => navigate("/configuracion/integraciones/editar/" + element_id)}
					>
						Ver Configuración
					</CustomButton>
					<CustomButton
						variant="secondary"
						disabled={isLoading}
						startIcon={<ShoppingCart size={16} />}
						onClick={() => navigate("/configuracion/integraciones/compras/" + element_id)}
					>
						Ver Compras
					</CustomButton>
				</>
			}
		>
			<ListScaffold
				data={data?.data || []}
				totalItems={data?.total || 0}
				totalPages={data?.last_page || 0}
				toItem={data?.to || 0}
				fromItem={data?.from || 0}
				isFilterOpen={isFilterOpen}
				filters={filtersState}
				onFilterChange={handleFilterChange}
				onApplyFilters={handleApplyFilters}
				onClearFilters={handleClearAllFilters}
				onClearOneFilter={handleClearOneFilter}
				onOpenFilter={openFilter}
				onCloseFilter={closeFilter}
				pageIndex={tableState.page}
				columns={columns}
				onPageChange={handlePageChange}
				pageSize={tableState.per_page}
				onPageSizeChange={handlePageSizeChange}
				sortBy={reactTableSort}
				onSortByChange={handleSortByChange}
				pageSizeOptions={[20, 50, 100]}
				initialPageSize={tableState.per_page}
				initialSortBy={reactTableSort}
				customHeight={"53vh"}
				customStyle={{ padding: 0 }}
				isLoading={isLoadingFinal}
			/>
		</CustomModalForm>
	);
};

export default IntegrationUsage;
